import { Link } from "react-router-dom";
import { UserX, Mail, ShieldCheck, Trash2, MoonStar, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CONTACT_EMAIL = "Virgispine@hotmail.it";

export default function EliminaAccount() {
  const { user } = useAuth();

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-14 sm:py-20" data-testid="elimina-account-page">
      <p className="eyebrow text-center">I tuoi dati, sotto il tuo controllo</p>
      <h1 className="font-display text-3xl sm:text-4xl text-amber-100 text-center mt-3">
        Eliminazione account e dati
      </h1>
      <p className="text-sm text-purple-200/70 text-center mt-4 max-w-xl mx-auto leading-relaxed">
        Questa pagina spiega come richiedere l'eliminazione del tuo account Arcana e dei dati associati.
        È accessibile a chiunque, anche senza avere l'app installata.
      </p>

      <div className="mystic-card mt-10 p-7 sm:p-10 space-y-10">
        <section data-testid="elimina-section-app">
          <h2 className="font-display text-xl text-amber-200 mb-4 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-amber-400" /> Elimina l'account dall'app (immediato)
          </h2>
          <p className="text-sm text-purple-100/75 leading-relaxed mb-4">
            Puoi eliminare il tuo account in autonomia, in qualsiasi momento, direttamente dall'app o dal sito:
          </p>
          <ol className="space-y-3 text-sm text-purple-100/75 leading-relaxed list-none" data-testid="elimina-steps">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full border border-amber-500/40 text-amber-300 text-xs flex items-center justify-center">1</span>
              <span>Accedi e apri la tua <strong className="text-amber-100/90">Area Cliente</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full border border-amber-500/40 text-amber-300 text-xs flex items-center justify-center">2</span>
              <span>Scorri fino alla sezione <strong className="text-amber-100/90">Privacy e dati</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full border border-amber-500/40 text-amber-300 text-xs flex items-center justify-center">3</span>
              <span>Tocca <strong className="text-amber-100/90">Elimina il mio account</strong> e conferma con la tua password. La cancellazione è immediata e definitiva.</span>
            </li>
          </ol>
          {user ? (
            <Link
              to="/cliente"
              data-testid="elimina-cliente-btn"
              className="gold-btn inline-flex items-center gap-2 px-7 py-3 mt-6 text-sm"
            >
              Vai alla tua Area Cliente <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <p className="text-xs text-purple-300/60 mt-6">
              Se stai navigando da browser, puoi anche <Link to="/accedi" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2" data-testid="elimina-login-link">accedere al tuo account dal sito</Link> e seguire gli stessi passaggi.
            </p>
          )}
        </section>

        <section data-testid="elimina-section-email">
          <h2 className="font-display text-xl text-amber-200 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-400" /> Richiesta via email
          </h2>
          <p className="text-sm text-purple-100/75 leading-relaxed mb-3">
            Se non riesci ad accedere all'app o preferisci un metodo alternativo, puoi richiedere la cancellazione
            scrivendo a{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Eliminazione account Arcana")}`}
              className="text-amber-300/90 hover:text-amber-200 underline underline-offset-2"
              data-testid="elimina-email-link"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            con oggetto <em>«Eliminazione account Arcana»</em>, possibilmente dall'indirizzo email associato al tuo account.
          </p>
          <p className="text-sm text-purple-100/75 leading-relaxed">
            La richiesta verrà evasa <strong className="text-amber-100/90">entro 30 giorni</strong> e riceverai conferma
            via email a cancellazione avvenuta.
          </p>
        </section>

        <section data-testid="elimina-section-dati-eliminati">
          <h2 className="font-display text-xl text-amber-200 mb-4 flex items-center gap-2">
            <UserX className="w-5 h-5 text-amber-400" /> Quali dati vengono eliminati
          </h2>
          <p className="text-sm text-purple-100/75 leading-relaxed mb-3">
            Con l'eliminazione dell'account vengono cancellati definitivamente:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-purple-100/75 leading-relaxed marker:text-amber-500/60">
            <li>il tuo profilo account (nome, indirizzo email e password cifrata);</li>
            <li>tutte le tue letture e interpretazioni (Sibille, Piacentine, Geomanzia, Rune);</li>
            <li>le conversazioni con Arcana, l'AI di approfondimento post-lettura;</li>
            <li>i messaggi privati scambiati con Virginia («Scrivi a Virginia»);</li>
            <li>le recensioni che hai inviato;</li>
            <li>i dati de «Il mio cielo» (data di nascita) e de «La nostra Coppia»;</li>
            <li>le prenotazioni e lo stato di abbonamento, crediti e prove gratuite.</li>
          </ul>
        </section>

        <section data-testid="elimina-section-dati-conservati">
          <h2 className="font-display text-xl text-amber-200 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-400" /> Dati eventualmente conservati
          </h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-purple-100/75 leading-relaxed marker:text-amber-500/60">
            <li>
              <strong className="text-amber-100/90">Pagamenti:</strong> sono gestiti interamente da Google Play;
              Arcana non conserva dati di pagamento. Le ricevute restano disponibili nel tuo account Google.
            </li>
            <li>
              <strong className="text-amber-100/90">Obblighi di legge:</strong> eventuali dati che la normativa
              impone di conservare (ad esempio a fini fiscali o contabili) vengono mantenuti solo per il tempo
              previsto dalla legge e poi eliminati.
            </li>
            <li>
              <strong className="text-amber-100/90">Backup tecnici:</strong> copie residue nei backup di sicurezza
              vengono eliminate entro 30 giorni dalla cancellazione.
            </li>
          </ul>
        </section>

        <section data-testid="elimina-section-sviluppatore">
          <h2 className="font-display text-xl text-amber-200 mb-4 flex items-center gap-2">
            <MoonStar className="w-5 h-5 text-amber-400" /> L'app e la sviluppatrice
          </h2>
          <p className="text-sm text-purple-100/75 leading-relaxed">
            <strong className="text-amber-100/90">Arcana</strong> è un'applicazione di cartomanzia (letture con
            Sibille, Piacentine, Geomanzia e Rune) sviluppata e gestita da <strong className="text-amber-100/90">Virginia</strong>,
            titolare del trattamento dei dati. Per qualsiasi domanda sulla cancellazione o sui tuoi dati puoi scrivere a{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-300/90 hover:text-amber-200 underline underline-offset-2">
              {CONTACT_EMAIL}
            </a>.
          </p>
        </section>

        <p className="text-xs text-purple-300/50 pt-4 border-t border-amber-500/10">
          Documenti collegati:{" "}
          <Link to="/privacy" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2" data-testid="elimina-privacy-link">Privacy Policy</Link> ·{" "}
          <Link to="/termini" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2">Termini e Condizioni</Link> ·{" "}
          <Link to="/disclaimer" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2">Disclaimer</Link>
        </p>
      </div>
    </div>
  );
}
